import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, Button, Text, Badge, tokens } from '../../design-system';
import { useUpdateRisks } from '../../hooks/api/useIdeas';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  mossSoft:  '#E8EDDB',
  mossDark:  '#4E5A2A',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

export const RISK_SEVERITY = {
  high:   { label: 'High',   color: T.capRed },
  medium: { label: 'Medium', color: T.orange },
  low:    { label: 'Low',    color: T.moss },
};

export const RISK_CATEGORIES = {
  regulatory:     { label: 'Regulatory & Compliance', icon: '⚖️' },
  cold_start:     { label: 'Cold Start & Distribution', icon: '🚀' },
  competitive:    { label: 'Competitive Landscape', icon: '🏆' },
  technical:      { label: 'Technical Complexity', icon: '🔧' },
  business_model: { label: 'Business Model', icon: '💼' },
  ux:             { label: 'UX & Adoption', icon: '👤' },
};

const RisksContent = ({ idea, onRefetch }) => {
  const updateRisksMutation = useUpdateRisks();

  const serverRisks = useMemo(() => idea.risks || [], [idea.risks]);
  const [localRisks, setLocalRisks] = useState(null);
  const risks = localRisks ?? serverRisks;

  // Sync when server data changes (e.g. after refetch)
  const serverRisksRef = React.useRef(serverRisks);
  useEffect(() => {
    if (serverRisks !== serverRisksRef.current) {
      setLocalRisks(serverRisks);
      serverRisksRef.current = serverRisks;
    }
  }, [serverRisks]);

  const [editingId, setEditingId] = useState(null);
  const [editFields, setEditFields] = useState({ title: '', description: '', mitigation: '' });
  const [adding, setAdding] = useState(false);
  const [newRisk, setNewRisk] = useState({ title: '', description: '', mitigation: '', severity: 'medium' });

  const save = useCallback((updated) => {
    setLocalRisks(updated);
    updateRisksMutation.mutate({ ideaId: idea.id, risks: updated }, {
      onSuccess: () => onRefetch?.(),
    });
  }, [updateRisksMutation, idea.id, onRefetch]);

  const handleEditStart = (r) => {
    setEditingId(r.id);
    setEditFields({ title: r.title || '', description: r.description || '', mitigation: r.mitigation || '' });
  };

  const handleEditSave = (id) => {
    const updated = risks.map(r =>
      r.id === id ? { ...r, ...editFields } : r
    );
    save(updated);
    setEditingId(null);
  };

  const handleDismiss = (id) => {
    save(risks.filter(r => r.id !== id));
  };

  const handleAdd = () => {
    if (!newRisk.title.trim()) return;
    const added = [...risks, {
      id: `r_custom_${Date.now()}`,
      title: newRisk.title.trim(),
      description: newRisk.description.trim(),
      mitigation: newRisk.mitigation.trim(),
      severity: newRisk.severity,
      category: 'technical',
      affects_scope: false,
    }];
    save(added);
    setNewRisk({ title: '', description: '', mitigation: '', severity: 'medium' });
    setAdding(false);
  };

  const riskInputStyle = {
    width: '100%',
    padding: '8px 10px',
    backgroundColor: T.paperSoft,
    border: `1px solid ${T.rule}`,
    borderRadius: T.r2,
    color: T.ink,
    fontSize: '13px',
    fontFamily: T.sans,
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'vertical',
    lineHeight: 1.6,
  };

  if (!risks.length) {
    return (
      <div style={{ padding: '32px', textAlign: 'center' }}>
        <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>⚠️</span>
        <div style={{ color: T.inkMuted, marginBottom: '16px', fontFamily: T.sans, fontSize: 14 }}>
          No considerations recorded yet.
        </div>
        <button
          onClick={() => setAdding(true)}
          style={{
            background: T.orange,
            border: 'none',
            borderRadius: T.r3,
            color: '#fff',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 600,
            padding: '7px 16px',
            fontFamily: T.sans,
          }}
        >
          + Add Consideration
        </button>
        {adding && (
          <div style={{ marginTop: '16px', textAlign: 'left' }}>
            <RiskAddForm
              newRisk={newRisk}
              setNewRisk={setNewRisk}
              onAdd={handleAdd}
              onCancel={() => { setAdding(false); setNewRisk({ title: '', description: '', mitigation: '', severity: 'medium' }); }}
              inputStyle={riskInputStyle}
            />
          </div>
        )}
      </div>
    );
  }

  // Group by severity (high → medium → low)
  const bySeverity = ['high', 'medium', 'low'].reduce((acc, s) => {
    const group = risks.filter(r => r.severity === s);
    if (group.length) acc[s] = group;
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {Object.entries(bySeverity).map(([severity, group]) => {
          const cfg = RISK_SEVERITY[severity] || { label: severity, color: T.inkMuted };
          return (
            <div key={severity}>
              {/* Group header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingBottom: 6, borderBottom: `1px dashed ${T.rule}` }}>
                <span style={{ width: 8, height: 8, borderRadius: T.full, background: cfg.color, display: 'inline-block' }}/>
                <div style={{ fontSize: 13, fontWeight: 800, color: T.ink2, textTransform: 'capitalize' }}>{cfg.label} priority</div>
                <div style={{ flex: 1 }}/>
                <div style={{ fontFamily: T.mono, fontSize: 10, color: T.inkMuted, fontWeight: 700 }}>{group.length}</div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {group.map(risk => {
                  const catCfg = RISK_CATEGORIES[risk.category] || { label: risk.category || 'General', icon: '📋' };
                  const isEditing = editingId === risk.id;
                  return (
                    <div key={risk.id} style={{ background: T.card, border: `1px solid ${T.rule}`, borderLeft: `3px solid ${cfg.color}`, borderRadius: T.r3, padding: 16 }}>
                      {isEditing ? (
                        <div>
                          <input
                            value={editFields.title}
                            onChange={(e) => setEditFields(f => ({ ...f, title: e.target.value }))}
                            placeholder="Title..."
                            style={{ ...riskInputStyle, fontWeight: 600, marginBottom: '8px', resize: 'none' }}
                          />
                          <textarea
                            value={editFields.description}
                            onChange={(e) => setEditFields(f => ({ ...f, description: e.target.value }))}
                            rows={3}
                            placeholder="Description..."
                            style={{ ...riskInputStyle, marginBottom: '8px' }}
                            autoFocus
                          />
                          <textarea
                            value={editFields.mitigation}
                            onChange={(e) => setEditFields(f => ({ ...f, mitigation: e.target.value }))}
                            rows={2}
                            placeholder="Mitigation (optional)..."
                            style={riskInputStyle}
                          />
                          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => setEditingId(null)}
                              style={{
                                background: 'transparent',
                                border: `1px dashed ${T.rule}`,
                                borderRadius: T.r2,
                                color: T.inkMuted,
                                cursor: 'pointer',
                                fontSize: '13px',
                                padding: '5px 12px',
                                fontFamily: T.sans,
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleEditSave(risk.id)}
                              disabled={updateRisksMutation.isPending}
                              style={{
                                background: T.orange,
                                border: 'none',
                                borderRadius: T.r3,
                                color: '#fff',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 600,
                                padding: '5px 14px',
                                fontFamily: T.sans,
                                opacity: updateRisksMutation.isPending ? 0.7 : 1,
                              }}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Category badge + title */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color, background: `${cfg.color}18`, border: `1px solid ${cfg.color}40`, borderRadius: T.full, padding: '2px 8px' }}>
                              {catCfg.label}
                            </span>
                            <div style={{ fontSize: 14, fontWeight: 800, color: T.ink, flex: 1 }}>{risk.title}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                              <button
                                onClick={() => handleEditStart(risk)}
                                style={{
                                  background: 'transparent',
                                  border: `1px dashed ${T.rule}`,
                                  borderRadius: T.r2,
                                  color: T.inkMuted,
                                  padding: '3px 8px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontFamily: T.sans,
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.rule; e.currentTarget.style.color = T.inkMuted; }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDismiss(risk.id)}
                                style={{
                                  background: 'none', border: 'none',
                                  color: T.inkFaint, cursor: 'pointer',
                                  fontSize: '16px', padding: '3px 6px',
                                  lineHeight: 1,
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = T.capRed; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = T.inkFaint; }}
                              >
                                &times;
                              </button>
                            </div>
                          </div>
                          {/* Description */}
                          {risk.description && (
                            <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.55, marginBottom: risk.mitigation ? 8 : 0 }}>{risk.description}</div>
                          )}
                          {/* Mitigation box */}
                          {risk.mitigation && (
                            <div style={{ padding: '8px 10px', background: T.mossSoft, borderRadius: T.r2, border: `1px dashed ${T.moss}` }}>
                              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, color: T.mossDark, textTransform: 'uppercase', marginBottom: 3 }}>Mitigation</div>
                              <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>{risk.mitigation}</div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add consideration */}
      <div style={{ marginTop: '16px' }}>
        {adding ? (
          <RiskAddForm
            newRisk={newRisk}
            setNewRisk={setNewRisk}
            onAdd={handleAdd}
            onCancel={() => { setAdding(false); setNewRisk({ title: '', description: '', mitigation: '', severity: 'medium' }); }}
            inputStyle={riskInputStyle}
            loading={updateRisksMutation.isPending}
          />
        ) : (
          <button
            onClick={() => setAdding(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              background: 'transparent', border: `1px dashed ${T.rule}`,
              borderRadius: T.r3, color: T.inkFaint,
              cursor: 'pointer', fontSize: '12px',
              padding: '8px 12px', width: '100%',
              justifyContent: 'center',
              fontFamily: T.sans,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.orange; e.currentTarget.style.color = T.orange; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.rule; e.currentTarget.style.color = T.inkFaint; }}
          >
            + Add consideration
          </button>
        )}
      </div>
    </div>
  );
};

const RiskAddForm = ({ newRisk, setNewRisk, onAdd, onCancel, inputStyle, loading }) => (
  <div style={{
    padding: '16px',
    backgroundColor: T.paperSoft,
    borderRadius: T.r3,
    border: `1px solid ${T.ruleStrong}`,
  }}>
    <input
      type="text"
      value={newRisk.title}
      onChange={(e) => setNewRisk(r => ({ ...r, title: e.target.value }))}
      placeholder="Risk title..."
      autoFocus
      style={{ ...inputStyle, fontWeight: 600, resize: 'none', marginBottom: '8px' }}
    />
    <textarea
      value={newRisk.description}
      onChange={(e) => setNewRisk(r => ({ ...r, description: e.target.value }))}
      rows={2}
      placeholder="Description — why does this matter?"
      style={{ ...inputStyle, marginBottom: '8px' }}
    />
    <textarea
      value={newRisk.mitigation}
      onChange={(e) => setNewRisk(r => ({ ...r, mitigation: e.target.value }))}
      rows={2}
      placeholder="Mitigation (optional)"
      style={{ ...inputStyle, marginBottom: '8px' }}
    />
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>
      <select
        value={newRisk.severity}
        onChange={(e) => setNewRisk(r => ({ ...r, severity: e.target.value }))}
        style={{
          padding: '5px 10px',
          backgroundColor: T.card,
          border: `1px solid ${T.rule}`,
          borderRadius: T.r2,
          color: T.ink,
          fontSize: '12px',
          fontFamily: T.sans,
          outline: 'none',
          cursor: 'pointer',
        }}
      >
        <option value="high">High severity</option>
        <option value="medium">Medium severity</option>
        <option value="low">Low severity</option>
      </select>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={onCancel}
          style={{
            background: 'transparent',
            border: `1px dashed ${T.rule}`,
            borderRadius: T.r2,
            color: T.inkMuted,
            cursor: 'pointer',
            fontSize: '13px',
            padding: '5px 12px',
            fontFamily: T.sans,
          }}
        >
          Cancel
        </button>
        <button
          onClick={onAdd}
          disabled={!newRisk.title.trim() || loading}
          style={{
            background: T.orange,
            border: 'none',
            borderRadius: T.r3,
            color: '#fff',
            cursor: newRisk.title.trim() ? 'pointer' : 'not-allowed',
            fontSize: '13px',
            fontWeight: 600,
            padding: '5px 14px',
            fontFamily: T.sans,
            opacity: (!newRisk.title.trim() || loading) ? 0.6 : 1,
          }}
        >
          Add
        </button>
      </div>
    </div>
  </div>
);

export default RisksContent;
